import axios from 'axios';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { api } from '../../Api';
import { Store } from '../../Store';
import { getError, otpGatewayLabel as otpProviderLabel } from '../../utils';
import { useDeveloperAccess } from '../../DeveloperAccess';
import DbEnvironmentBadge from '../../component/DbEnvironmentBadge';
import {
  STICKER_PRESETS,
  presetIdFor,
  resolveSticker,
  sheetPlan,
  stickerPreviewSvg,
} from '../../utils/stickerPdf';

// Hidden developer settings page. Reached only by typing /julkarnine in the URL
// (never linked in any sidebar) and then entering the developer PIN.

// Mirrors MAX_SMS_CHANNELS on the server.
const MAX_SMS_CHANNELS = 5;

const GATES = [
  { key: 'login', label: 'Login' },
  { key: 'withdraw', label: 'Withdraw' },
  { key: 'convert', label: 'Convert' },
  { key: 'transfer', label: 'Transfer' },
  { key: 'associationTransfer', label: 'Merchant Transfer' },
  { key: 'moneyRequest', label: 'Request Money approve' },
  { key: 'deliveryRequest', label: 'Delivery request' },
  { key: 'deliveryHandover', label: 'Delivery hand-over' },
];

// Delivery switches, rendered as a list so adding one later is a single line.
const DELIVERY_SWITCHES = [
  {
    key: 'requestEnabled',
    label: 'Enable delivery requests',
    hint: 'Off hides the request button for every customer.',
  },
  {
    key: 'allowPartial',
    label: 'Allow partial collection',
    hint: 'Off forces a request to cover everything still owed on that item.',
  },
  {
    key: 'allowAdminEditQuantity',
    label: 'Staff can edit the quantity',
    hint: 'Lets the counter hand over more or fewer units than the customer asked for.',
  },
  {
    key: 'requireFingerprint',
    label: 'Require fingerprint at hand-over',
    hint: 'The counter must match a finger before stock can move.',
  },
  {
    key: 'allowFingerprintOverride',
    label: 'Allow fingerprint override',
    hint: 'Staff may skip a failing scan by typing a reason. Every override is logged.',
  },
  {
    key: 'requireSerialScan',
    label: 'Require scanning each unit',
    hint: 'Off assigns the oldest units automatically instead of scanning stickers.',
  },
  {
    key: 'requireOrderApproved',
    label: 'Only collect approved orders',
    hint: 'Off lets customers collect before an admin approves the payment.',
  },
  {
    key: 'allowUserCancel',
    label: 'Customers can cancel a request',
    hint: 'Off means only staff can reject an uncollected request.',
  },
  {
    key: 'chargeUserForOtp',
    label: 'Charge the customer for delivery OTPs',
    hint: 'Off (recommended) — staff-initiated codes should not bill the customer.',
  },
];

const STOCK_SWITCHES = [
  {
    key: 'reserveOnOrder',
    label: 'Reserve stock when an order is placed',
    hint: 'Keeps two customers from claiming the same piece. Available = shelf − reserved.',
  },
  {
    key: 'blockOrderWhenOutOfStock',
    label: 'Block orders the shelf cannot cover',
    hint: 'Off lets customers order beyond available stock.',
  },
  {
    key: 'allowReprint',
    label: 'Allow sticker reprints',
    hint: 'Off refuses to print a sticker for a unit that already has one.',
  },
  { key: 'includeYearInSerial', label: 'Put the year in serials', hint: 'CC-2026-000123 vs CC-000123.' },
];

// Every line that can appear on a sticker. Anything switched off is removed and
// the remaining blocks re-flow to fill the label.
const STICKER_ELEMENTS = [
  { key: 'showHeader', label: 'Header line', hint: 'A shop or brand name across the top.' },
  { key: 'showProductName', label: 'Product name', hint: 'Wraps to the line limit set below.' },
  { key: 'showPrice', label: 'Price', hint: 'Uses the currency label below.' },
  { key: 'showPoint', label: 'Points (PV)', hint: '' },
  { key: 'showBrand', label: 'Brand', hint: 'Small grey line under the name.' },
  { key: 'showCategory', label: 'Category', hint: 'Shown beside the brand.' },
  { key: 'showBarcode', label: 'Barcode', hint: 'Code 128. Turning this off makes the unit unscannable.' },
  { key: 'showSerialText', label: 'Serial number text', hint: 'Readable serial under the bars.' },
  { key: 'showBatch', label: 'Batch code', hint: 'In the footer line.' },
  { key: 'showDate', label: 'Date', hint: 'Format chosen below.' },
  { key: 'showFooter', label: 'Company name', hint: 'Right of the footer line.' },
  { key: 'showCutGuides', label: 'Cut guides', hint: 'Dashed trim outline. Useful on plain paper, noise on die-cut labels.' },
];

// Curated parity list from the mobile App Control centre. These 50 toggles are
// shown in web even before the device-published registry is available.
const APP_CONTROL_TOP_50 = [
  'all.auth.signIn',
  'all.auth.signUp',
  'all.auth.forgotPassword',
  'all.auth.onboarding',
  'all.auth.consent',
  'all.auth#biometricUnlock',
  'all.app#locationCapture',
  'all.app#rootBlock',
  'all.app#screenshotBlock',
  'all.app#deviceTelemetry',
  'member.home',
  'member.home.notifications',
  'member.home.chat',
  'member.team',
  'member.team.addMember',
  'member.wallet',
  'member.wallet.transfer',
  'member.wallet.convert',
  'member.wallet.withdraw',
  'member.wallet.history',
  'member.shop',
  'member.shop.search',
  'member.shop.product',
  'member.shop.cart',
  'member.shop.checkout',
  'member.shop.orders',
  'member.promotion.status',
  'member.promotion.ladder',
  'member.sales.mine',
  'member.profile.edit',
  'member.profile.bank',
  'member.profile.kyc',
  'member.profile.password',
  'all.profile.settings',
  'all.profile.security',
  'agent.home',
  'agent.orders',
  'agent.withdrawals',
  'agent.products',
  'manager.users',
  'manager.search',
  'manager.report',
  'admin.dashboard',
  'admin.users',
  'admin.money',
  'admin.ops',
  'admin.backup',
  'admin.config',
  'admin.appControl',
  'admin.push',
];

function fallbackControlDef(key) {
  const [role = 'all'] = String(key || '').split('.');
  const area = String(key || '').split(/[.#]/).slice(0, 2).join('.');
  return {
    key,
    role,
    area,
    label: key,
    kind: 'screen',
    defaultEnabled: true,
    locked: false,
  };
}

export default function DeveloperScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;

  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState('');
  const [topupAmount, setTopupAmount] = useState('');
  const [newPin, setNewPin] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [otpLog, setOtpLog] = useState(null);
  const [appConfig, setAppConfig] = useState(null);
  const [configSaving, setConfigSaving] = useState(false);
  const [registryVersion, setRegistryVersion] = useState(0);
  const [controlRegistry, setControlRegistry] = useState([]);
  const [controlState, setControlState] = useState({});
  const [controlQuery, setControlQuery] = useState('');
  const [controlsSaving, setControlsSaving] = useState(false);

  // The developer PIN is entered once by DeveloperGate, which holds the unlock
  // ticket for the whole developer area. This screen just borrows those headers.
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${userInfo?.token}` }),
    [userInfo]
  );
  const { devHeaders } = useDeveloperAccess();

  const loadSettings = useCallback(async () => {
    const { data } = await axios.get(api + '/api/developer/settings', { headers: devHeaders });
    setSettings(data);
  }, [devHeaders]);

  const loadAppControlData = useCallback(async () => {
    const [configRes, registryRes] = await Promise.all([
      axios.get(api + '/api/app/config', { headers: authHeaders }),
      axios.get(api + '/api/app/controls/registry', { headers: authHeaders }),
    ]);
    setAppConfig(configRes.data || null);
    setRegistryVersion(Number(registryRes.data?.registryVersion || 0));
    setControlRegistry(Array.isArray(registryRes.data?.definitions) ? registryRes.data.definitions : []);
    setControlState(registryRes.data?.controls && typeof registryRes.data.controls === 'object' ? registryRes.data.controls : {});
  }, [authHeaders]);

  // Already past the gate by the time this renders, so load immediately.
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        await loadSettings();
        if (!live) return;
        await loadAppControlData();
      } catch (err) {
        if (live) toast.error(getError(err));
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateAppConfig = (path, value) => {
    setAppConfig((cur) => {
      const next = structuredClone(cur || {});
      let node = next;
      const parts = path.split('.');
      for (let i = 0; i < parts.length - 1; i += 1) {
        node[parts[i]] = node[parts[i]] ?? {};
        node = node[parts[i]];
      }
      node[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const saveAppRuntime = async () => {
    if (!appConfig?.app) return;
    setConfigSaving(true);
    try {
      const payload = {
        app: {
          enabled: appConfig.app.enabled !== false,
          loginEnabled: appConfig.app.loginEnabled !== false,
        },
      };
      const { data } = await axios.put(api + '/api/app/config', payload, { headers: authHeaders });
      if (data?.config) setAppConfig(data.config);
      toast.success(data?.message || 'App runtime saved.');
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setConfigSaving(false);
    }
  };

  const resolveControl = (def) => {
    const current = controlState?.[def.key] || {};
    return {
      enabled: current.enabled !== undefined ? current.enabled !== false : def.defaultEnabled !== false,
      hidden: Boolean(current.hidden),
      message: String(current.message || ''),
      requireOtp: Boolean(current.requireOtp),
      requireBiometric: Boolean(current.requireBiometric),
      disabledFrom: current.disabledFrom || null,
      disabledUntil: current.disabledUntil || null,
      minDesignation: current.minDesignation ?? null,
    };
  };

  const updateControl = (def, field, value) => {
    setControlState((cur) => {
      const next = { ...(cur || {}) };
      next[def.key] = {
        ...resolveControl(def),
        ...next[def.key],
        [field]: value,
      };
      return next;
    });
  };

  const saveControls = async () => {
    setControlsSaving(true);
    try {
      const { data } = await axios.put(
        api + '/api/app/controls',
        { controls: controlState, registryVersion },
        { headers: authHeaders },
      );
      if (data?.controls) setControlState(data.controls);
      toast.success(data?.message || 'App controls saved.');
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setControlsSaving(false);
    }
  };

  const allControlDefs = useMemo(() => {
    const byKey = new Map();
    for (const key of APP_CONTROL_TOP_50) byKey.set(key, fallbackControlDef(key));
    for (const def of controlRegistry || []) {
      if (!def || !def.key) continue;
      byKey.set(def.key, { ...fallbackControlDef(def.key), ...def });
    }
    return [...byKey.values()];
  }, [controlRegistry]);

  const top50Defs = useMemo(() => {
    const fromCatalog = new Map(allControlDefs.map((d) => [d.key, d]));
    return APP_CONTROL_TOP_50.map((key) => fromCatalog.get(key) || fallbackControlDef(key));
  }, [allControlDefs]);

  const setTop50Enabled = (enabled) => {
    setControlState((cur) => {
      const next = { ...(cur || {}) };
      for (const def of top50Defs) {
        next[def.key] = {
          ...resolveControl(def),
          ...next[def.key],
          enabled,
        };
      }
      return next;
    });
  };

  const visibleControls = useMemo(() => {
    const q = controlQuery.trim().toLowerCase();
    const sorted = [...allControlDefs].sort((a, b) => {
      const aa = `${a.role || ''} ${a.area || ''} ${a.label || ''} ${a.key || ''}`.toLowerCase();
      const bb = `${b.role || ''} ${b.area || ''} ${b.label || ''} ${b.key || ''}`.toLowerCase();
      return aa.localeCompare(bb);
    });
    if (!q) return sorted;
    return sorted.filter((d) => {
      const hay = `${d.key || ''} ${d.label || ''} ${d.area || ''} ${d.role || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [allControlDefs, controlQuery]);

  const update = (path, value) => {
    setSettings((cur) => {
      const next = structuredClone(cur);
      let node = next;
      const parts = path.split('.');
      for (let i = 0; i < parts.length - 1; i += 1) {
        node[parts[i]] = node[parts[i]] ?? {};
        node = node[parts[i]];
      }
      node[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const setAdminNumber = (i, val) => {
    setSettings((cur) => {
      const arr = [...(cur.adminAlertNumbers || [])];
      while (arr.length < 3) arr.push('');
      arr[i] = val;
      return { ...cur, adminAlertNumbers: arr };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(api + '/api/developer/settings', settings, { headers: devHeaders });
      setSettings(data.settings);
      toast.success(data.message || 'Saved.');
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setSaving(false);
    }
  };

  const runTest = async (provider, sessionId = '') => {
    if (!testPhone.trim()) return toast.error('Enter a phone number to test.');
    setBusy(`test-${provider}`);
    try {
      const { data } = await axios.post(
        api + `/api/developer/test/${provider}`,
        // Naming the number tests THAT number, instead of quietly succeeding
        // through a fallback and leaving a broken one looking healthy.
        { phone: testPhone, sessionId, text: `ClickCart ${otpProviderLabel(provider)} test ✅` },
        { headers: devHeaders }
      );
      if (data.ok) toast.success(`${otpProviderLabel(provider)}: sent ✅`);
      else toast.error(`${otpProviderLabel(provider)}: ${data.detail || 'failed'}`);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy('');
    }
  };

  // --- SMS gateways ---------------------------------------------------------
  // Mirrors smsChannelOrder() on the server: enabled rows that could actually
  // send, in the order a real send walks them. Shown so the screen states the
  // live sending order rather than leaving it to be inferred from the list.
  const smsOrder = useMemo(
    () => (settings?.smsChannels || []).filter(
      (c) => c && c.enabled && String(c.apiKey || '').trim() && String(c.baseUrl || '').trim()
    ),
    [settings?.smsChannels]
  );

  // The list is ordered and the order is the failover order, so editing it
  // means editing positions as well as fields.
  const updateSmsChannel = (ci, key, value) => {
    setSettings((cur) => {
      const next = structuredClone(cur);
      next.smsChannels = next.smsChannels || [];
      next.smsChannels[ci] = { ...next.smsChannels[ci], [key]: value };
      return next;
    });
  };

  const addSmsChannel = () => {
    setSettings((cur) => {
      const next = structuredClone(cur);
      next.smsChannels = next.smsChannels || [];
      if (next.smsChannels.length >= MAX_SMS_CHANNELS) return cur;
      next.smsChannels.push({
        provider: 'pitech',
        label: `Gateway ${next.smsChannels.length + 1}`,
        // Off until it has been tested. Saving an untested gateway at the top
        // of the order is how every SMS ends up waiting for it to fail.
        enabled: false,
        baseUrl: 'https://besends.com/api',
        apiKey: '',
        gatewayIdentifier: '',
        smsType: '',
        method: '',
        timeoutMs: 12000,
        whatsappEnabled: false,
        whatsappRoute: 'auto',
        whatsappGatewayIdentifier: '',
        whatsappTemplate: '',
      });
      return next;
    });
  };

  const removeSmsChannel = (ci) => {
    setSettings((cur) => {
      const next = structuredClone(cur);
      next.smsChannels = (next.smsChannels || []).filter((_, i) => i !== ci);
      return next;
    });
  };

  // Position IS priority, so this is the control that decides which gateway
  // carries the traffic — not a cosmetic sort.
  const moveSmsChannel = (ci, delta) => {
    setSettings((cur) => {
      const next = structuredClone(cur);
      const list = next.smsChannels || [];
      const to = ci + delta;
      if (to < 0 || to >= list.length) return cur;
      [list[ci], list[to]] = [list[to], list[ci]];
      return next;
    });
  };

  // Free: proves the API key is accepted without queueing a message, so it can
  // be pressed as often as it takes to get the address and key right.
  const testSmsConnection = async (ci) => {
    const channel = (settings?.smsChannels || [])[ci];
    if (!channel) return;
    setBusy(`sms-conn-${ci}`);
    try {
      const { data } = await axios.post(
        api + '/api/developer/sms/test-connection',
        {
          // Sent from the form rather than read from the database, so a gateway
          // can be checked before it is saved.
          baseUrl: channel.baseUrl,
          apiKey: channel.apiKey,
          timeoutMs: channel.timeoutMs,
          channelId: channel._id,
        },
        { headers: devHeaders }
      );
      if (data.ok) toast.success(data.detail || 'Connected.');
      else toast.error(data.detail || 'Could not connect.');
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy('');
    }
  };

  const topup = async () => {
    const amount = Number(topupAmount);
    if (!amount) return toast.error('Enter a non-zero amount.');
    setBusy('topup');
    try {
      const { data } = await axios.post(api + '/api/developer/balance/topup', { amount }, { headers: devHeaders });
      update('balanceBdt', data.balanceBdt);
      setTopupAmount('');
      toast.success(data.message);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy('');
    }
  };

  const changePin = async () => {
    if (!/^\d{5,8}$/.test(newPin)) return toast.error('PIN must be 5 to 8 digits.');
    setBusy('pin');
    try {
      const { data } = await axios.post(api + '/api/developer/pin', { newPin }, { headers: devHeaders });
      // The current unlock ticket stays valid, so changing the PIN does not
      // kick the admin out of the page they just changed it from.
      toast.success(data.message);
      setNewPin('');
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy('');
    }
  };

  const loadLog = async () => {
    setBusy('log');
    try {
      const { data } = await axios.get(api + '/api/developer/otp-log', { headers: devHeaders });
      setOtpLog(data);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy('');
    }
  };

  if (!settings) return <div style={s.wrap}>Loading…</div>;

  const balance = Number(settings.balanceBdt || 0);
  const threshold = Number(settings.lowBalanceThreshold || 1000);

  return (
    <div style={s.wrap}>
      <Helmet><title>App Control</title></Helmet>
      <div style={s.headerRow}>
        <div>
          <h3 style={{ margin: 0 }}>App Control Centre</h3>
          <p style={s.muted}>Central control panel for OTP, delivery, stock, messaging, and system runtime behavior.</p>
          {/* Nearly everything below this line spends money or changes what
              real customers see. Which database that lands in belongs at the
              top of the page, not somewhere further down it. */}
          <DbEnvironmentBadge detailed />
        </div>
        <div style={{ ...s.balancePill, ...(balance < threshold ? s.balanceDanger : s.balanceOk) }}>
          <span style={{ fontSize: 12, opacity: 0.8 }}>OTP Balance</span>
          <strong style={{ fontSize: 20 }}>৳ {balance.toFixed(2)}</strong>
          {balance < threshold && <span style={{ fontSize: 11 }}>Low — top up soon</span>}
        </div>
      </div>

      {/* OTP GATES */}
      <Section title="OTP Gates" subtitle="Turn OTP on per action. When off, that action keeps its previous behaviour.">
        <div style={s.grid}>
          {GATES.map((g) => (
            <div key={g.key} style={s.gateCardMini}>
              <div style={s.gateTop}>
                <strong>{g.label}</strong>
                <Toggle on={settings.gates?.[g.key]?.enabled} onClick={() => update(`gates.${g.key}.enabled`, !settings.gates?.[g.key]?.enabled)} />
              </div>
              <label style={s.checkRow}>
                <input
                  type="checkbox"
                  checked={Boolean(settings.gates?.[g.key]?.allowPassword)}
                  disabled={!settings.gates?.[g.key]?.enabled}
                  onChange={(e) => update(`gates.${g.key}.allowPassword`, e.target.checked)}
                />
                <span>Also accept password in the OTP field</span>
              </label>
            </div>
          ))}
        </div>
      </Section>

      {/* REQUEST MONEY */}
      <Section
        title="Request Money"
        subtitle="Members ask an upline merchant for Shopping Balance; the merchant approves from Withdraw Requests. The OTP for approving is the 'Request Money approve' gate above."
      >
        <div style={s.grid}>
          <SwitchRow
            label="Request Money WhatsApp notifications"
            hint="Off sends no WhatsApp for requested, approved or rejected (the approval message carries the receipt link). In-app notifications still go out."
            on={settings.moneyRequest?.whatsappNotify !== false}
            onToggle={(next) => update('moneyRequest.whatsappNotify', next)}
          />
        </div>
      </Section>

      {/* TOPUP UNDO (temporary) */}
      <Section
        title="Topup undo (temporary)"
        subtitle="Shows an Undo button on approved topups in Settings → Topup Requests. Undo takes the same Tk out of the member's Shopping Balance and adds it to their Available Balance — nothing else changes."
      >
        <div style={s.grid}>
          <SwitchRow
            label="Show Undo on approved topups"
            hint="Off hides the button and the server refuses any undo."
            on={Boolean(settings.topupUndo?.enabled)}
            onToggle={(next) => update('topupUndo.enabled', next)}
          />
          <SwitchRow
            label="Allow negative Shopping Balance"
            hint="Off: an undo is refused when the member has already spent part of that Shopping Balance. On: it goes through and the Shopping Balance can go below zero."
            on={Boolean(settings.topupUndo?.allowNegative)}
            onToggle={(next) => update('topupUndo.allowNegative', next)}
          />
        </div>
      </Section>

      {/* DELIVERY FLOW */}
      <Section
        title="Delivery Flow"
        subtitle="Each step of the counter hand-over can be switched on or off independently. A step that is off is skipped entirely — the wizard adapts."
      >
        <div style={s.grid}>
          {DELIVERY_SWITCHES.map((item) => (
            <SwitchRow
              key={item.key}
              label={item.label}
              hint={item.hint}
              on={
                item.key === 'chargeUserForOtp'
                  ? Boolean(settings.delivery?.[item.key])
                  : settings.delivery?.[item.key] !== false
              }
              onToggle={(next) => update(`delivery.${item.key}`, next)}
            />
          ))}
        </div>

        <div style={{ ...s.grid, marginTop: 14 }}>
          <Field
            label="Failed scans before override is offered"
            type="number"
            value={settings.delivery?.maxFingerprintAttempts}
            onChange={(v) => update('delivery.maxFingerprintAttempts', v)}
          />
          <Field
            label="Smallest request quantity"
            type="number"
            value={settings.delivery?.minRequestQuantity}
            onChange={(v) => update('delivery.minRequestQuantity', v)}
          />
          <Field
            label="Largest request quantity (0 = no limit)"
            type="number"
            value={settings.delivery?.maxRequestQuantity}
            onChange={(v) => update('delivery.maxRequestQuantity', v)}
          />
          <Field
            label="Open requests per customer (0 = unlimited)"
            type="number"
            value={settings.delivery?.maxOpenRequestsPerUser}
            onChange={(v) => update('delivery.maxOpenRequestsPerUser', v)}
          />
          <Field
            label="Wait between requests (minutes)"
            type="number"
            value={settings.delivery?.requestCooldownMinutes}
            onChange={(v) => update('delivery.requestCooldownMinutes', v)}
          />
        </div>
      </Section>

      {/* FINGERPRINT */}
      <Section
        title="Fingerprint Scanner (ZKTeco SLK20R)"
        subtitle="The scanner is a USB device, so a small bridge service runs on the delivery-desk PC and the browser talks to it on localhost. See FINGERPRINT_BRIDGE.md for the contract it must implement."
      >
        <div style={s.grid}>
          <SwitchRow
            label="Fingerprint hardware enabled"
            hint="Off means the scanner is not used anywhere."
            on={Boolean(settings.fingerprint?.enabled)}
            onToggle={(next) => update('fingerprint.enabled', next)}
          />
          <SwitchRow
            label="Simulation mode"
            hint="No hardware needed — captures and matches are faked so the flow can be rehearsed. Every simulated result is labelled as such in the log and never counts as proof of identity."
            on={settings.fingerprint?.simulationMode !== false}
            onToggle={(next) => update('fingerprint.simulationMode', next)}
          />
          <SwitchRow
            label="Allow enrolment at the counter"
            hint="Offers 'Enrol now' when a customer arrives without a fingerprint on file."
            on={settings.fingerprint?.allowEnrollAtCounter !== false}
            onToggle={(next) => update('fingerprint.allowEnrollAtCounter', next)}
          />
        </div>

        <div style={{ ...s.grid, marginTop: 14 }}>
          <Field
            label="Bridge address"
            value={settings.fingerprint?.bridgeUrl}
            onChange={(v) => update('fingerprint.bridgeUrl', v)}
            placeholder="http://127.0.0.1:8712"
          />
          <Field
            label={`Bridge shared secret ${settings.fingerprint?.bridgeSecretSet ? '(set — type to replace)' : '(not set)'}`}
            value={settings.fingerprint?.bridgeSecret}
            onChange={(v) => update('fingerprint.bridgeSecret', v)}
            placeholder="Leave blank to keep the current secret"
            secret
          />
          <Field
            label="Match threshold (0-100)"
            type="number"
            value={settings.fingerprint?.matchThreshold}
            onChange={(v) => update('fingerprint.matchThreshold', v)}
          />
          <Field
            label="Capture timeout (seconds)"
            type="number"
            value={settings.fingerprint?.captureTimeoutSeconds}
            onChange={(v) => update('fingerprint.captureTimeoutSeconds', v)}
          />
          <Field
            label="Fingers to enrol per customer"
            type="number"
            value={settings.fingerprint?.fingersToEnroll}
            onChange={(v) => update('fingerprint.fingersToEnroll', v)}
          />
        </div>

        {!settings.fingerprint?.bridgeSecretSet && settings.fingerprint?.simulationMode === false && (
          <p style={{ ...s.muted, color: '#b45309', marginTop: 10 }}>
            No shared secret is set, so the server cannot prove a match really came from the scanner.
            Results will still be accepted but are recorded as untrusted.
          </p>
        )}
      </Section>

      {/* DELIVERY CHARGE */}
      <Section
        title="Delivery Charge"
        subtitle="Added to shop orders for home delivery. Off means free delivery. Store pickup is always free. The server prices every order with this rule, and checkout shows it before the member confirms."
      >
        <div style={s.providerBox}>
          <div style={s.gateTop}>
            <strong>
              <i className="fa fa-truck" style={{ color: '#2563eb' }} /> Charge for home delivery
            </strong>
            <Toggle
              on={Boolean(settings.deliveryCharge?.enabled)}
              onClick={() => update('deliveryCharge.enabled', !settings.deliveryCharge?.enabled)}
            />
          </div>
          <div style={s.smsPickRow}>
            {[
              { key: 'per_item', label: 'Per item', sub: 'Amount × number of units' },
              { key: 'per_order', label: 'Per order', sub: 'One flat amount' },
            ].map((option) => {
              const chosen = (settings.deliveryCharge?.mode || 'per_item') === option.key;
              return (
                <button
                  key={option.key}
                  style={chosen ? s.smsPickOn : s.smsPick}
                  onClick={() => update('deliveryCharge.mode', option.key)}
                >
                  <span style={{ fontWeight: 700 }}>
                    <i className={chosen ? 'fa fa-dot-circle-o' : 'fa fa-circle-o'} style={{ marginRight: 7 }} />
                    {option.label}
                  </span>
                  <span style={{ fontSize: 12, opacity: 0.75 }}>{option.sub}</span>
                </button>
              );
            })}
          </div>
          <div style={s.grid}>
            <Field
              label="Amount (BDT)"
              type="number"
              value={settings.deliveryCharge?.amount ?? 60}
              onChange={(v) => update('deliveryCharge.amount', v)}
            />
          </div>
          <div style={s.hint}>
            {settings.deliveryCharge?.enabled
              ? `Now: ৳${Number(settings.deliveryCharge?.amount) || 0} ${
                  (settings.deliveryCharge?.mode || 'per_item') === 'per_order' ? 'per order' : 'per item'
                } for home delivery.`
              : 'Now: delivery is free.'}{' '}
            Save to apply.
          </div>
        </div>
      </Section>

      {/* STOCK */}
      <Section title="Stock & Serials" subtitle="How stock behaves and how serial numbers are shaped.">
        <div style={s.grid}>
          {STOCK_SWITCHES.map((item) => (
            <SwitchRow
              key={item.key}
              label={item.label}
              hint={item.hint}
              on={settings.stock?.[item.key] !== false}
              onToggle={(next) => update(`stock.${item.key}`, next)}
            />
          ))}
        </div>

        <div style={{ ...s.grid, marginTop: 14 }}>
          <Field
            label="Serial prefix"
            value={settings.stock?.serialPrefix}
            onChange={(v) => update('stock.serialPrefix', v)}
            placeholder="CC"
          />
          <Field
            label="Serial digits"
            type="number"
            value={settings.stock?.serialPadding}
            onChange={(v) => update('stock.serialPadding', v)}
          />
          <Field
            label="Low stock warning below"
            type="number"
            value={settings.stock?.lowStockThreshold}
            onChange={(v) => update('stock.lowStockThreshold', v)}
          />
        </div>

        <p style={{ ...s.muted, marginTop: 12 }}>
          Next serial looks like{' '}
          <strong>
            {(settings.stock?.serialPrefix || 'CC')}
            {settings.stock?.includeYearInSerial === false ? '' : `-${new Date().getFullYear()}`}
            -{'1'.padStart(Number(settings.stock?.serialPadding) || 6, '0')}
          </strong>
        </p>
      </Section>

      {/* STICKER DESIGNER */}
      <StickerDesigner
        design={settings.stock?.sticker}
        onChange={(key, value) => update(`stock.sticker.${key}`, value)}
      />

      {/* SMS GATEWAYS */}
      <Section
        title="BeSends Gateways (SMS & WhatsApp)"
        subtitle="OTP codes go out by SMS. Business messages (withdraw, convert, purchase, topup) use SMS and/or WhatsApp in the order set in the Message Center. Order here is gateway priority: the first enabled gateway is tried, the ones under it only pick up what it could not send."
      >
        {/* SMS gateways. Everything about one — address, key, route, position
            in the failover order — is edited here rather than in a deploy. */}
        <div style={s.providerBox}>
          <div style={s.gateTop}>
            <strong><i className="fa fa-commenting" style={{ color: '#2563eb' }} /> SMS gateways</strong>
            <button
              style={s.secondaryBtn}
              onClick={addSmsChannel}
              disabled={(settings.smsChannels || []).length >= MAX_SMS_CHANNELS}
            >
              <i className="fa fa-plus" /> Add gateway
            </button>
          </div>
          <div style={s.hint}>
            These carry every message the system sends: OTP codes, resends, admin
            alerts and topup notices alike. The order below is the
            order they are tried in — the first enabled gateway goes first, and the
            ones under it only pick up what it could not send.
          </div>

          {(settings.smsChannels || []).length === 0 ? (
            <div style={s.warnBox}>
              No SMS gateway is registered, so nothing can go out by SMS at all. Add
              one, paste its API key, test the connection, then switch it on.
            </div>
          ) : null}

          {smsOrder.length === 0 && (settings.smsChannels || []).length > 0 ? (
            <div style={s.warnBox}>
              Every gateway is switched off or missing its API key. Nothing can go out
              by SMS until at least one is on.
            </div>
          ) : null}

          {smsOrder.length > 0 ? (
            <div style={s.hint}>
              Sending order right now: {smsOrder.map((c, i) => `${i + 1}. ${c.label || 'Unnamed'}`).join('  →  ')}
              {settings.smsFallback === false && smsOrder.length > 1
                ? ' — but fallback is off, so only the first one is ever tried.'
                : ''}
            </div>
          ) : null}

          <div style={s.gateTop}>
            <span>
              <strong>Fall back to the next gateway</strong>
              <div style={s.hint}>
                Off means the first enabled gateway gets one attempt and a failed SMS
                is simply a failed SMS.
              </div>
            </span>
            <Toggle
              on={settings.smsFallback !== false}
              onClick={() => update('smsFallback', settings.smsFallback === false)}
            />
          </div>
        </div>

        {(settings.smsChannels || []).map((channel, ci) => {
          // Where this row sits in the order that a real send will actually
          // walk, as opposed to where it sits in the list.
          const rank = smsOrder.findIndex((c) => c === channel);
          return (
            <div style={s.providerBox} key={channel._id || ci}>
              <div style={s.gateTop}>
                <strong>
                  <i className="fa fa-paper-plane" style={{ color: '#2563eb' }} />{' '}
                  {channel.label || `Gateway ${ci + 1}`}
                  {rank === 0 ? <span style={s.primaryTag}>PRIMARY</span> : null}
                  {rank > 0 ? <span style={s.primaryTag}>FALLBACK {rank}</span> : null}
                </strong>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {/* Position is priority, so these are settings, not cosmetics. */}
                  <button style={s.iconBtn} onClick={() => moveSmsChannel(ci, -1)} disabled={ci === 0} title="Try this one earlier">
                    <i className="fa fa-arrow-up" />
                  </button>
                  <button
                    style={s.iconBtn}
                    onClick={() => moveSmsChannel(ci, 1)}
                    disabled={ci === (settings.smsChannels || []).length - 1}
                    title="Try this one later"
                  >
                    <i className="fa fa-arrow-down" />
                  </button>
                  <button style={s.iconBtn} onClick={() => removeSmsChannel(ci)} title="Remove this gateway">
                    <i className="fa fa-trash" />
                  </button>
                  <Toggle on={channel.enabled} onClick={() => updateSmsChannel(ci, 'enabled', !channel.enabled)} />
                </div>
              </div>

              {channel.enabled && !String(channel.apiKey || '').trim() ? (
                <div style={s.warnBox}>
                  This gateway has no API key, so it will be switched off again when you
                  save. Paste the key first.
                </div>
              ) : null}

              <div style={s.grid}>
                <Field
                  label="Name (yours, so you can tell accounts apart)"
                  value={channel.label}
                  onChange={(v) => updateSmsChannel(ci, 'label', v)}
                  placeholder="BeSends"
                />
                <Field
                  label="API address"
                  value={channel.baseUrl}
                  onChange={(v) => updateSmsChannel(ci, 'baseUrl', v)}
                  placeholder="https://besends.com/api"
                />
              </div>
              <Field
                label="API key"
                value={channel.apiKey}
                onChange={(v) => updateSmsChannel(ci, 'apiKey', v)}
                placeholder="paste the key from BeSends → Developer → API key"
                secret
              />
              <div style={s.hint}>
                From the BeSends panel (Developer → API key). It is sent in the <code>Api-key</code> header,
                never in a URL. Paste the address however it comes — the docs link, the
                bare host or the full <code>/sms/send</code> line all work.
              </div>

              <div style={s.grid}>
                <Field
                  label="Gateway identifier (optional)"
                  value={channel.gatewayIdentifier}
                  onChange={(v) => updateSmsChannel(ci, 'gatewayIdentifier', v)}
                  placeholder="leave blank for the account default"
                />
                <Field
                  label="SMS type (optional)"
                  value={channel.smsType}
                  onChange={(v) => updateSmsChannel(ci, 'smsType', v)}
                  placeholder="leave blank for the account default"
                />
              </div>
              <div style={s.grid}>
                <Field
                  label="Give up after (ms, 2000–12000)"
                  type="number"
                  value={channel.timeoutMs}
                  onChange={(v) => updateSmsChannel(ci, 'timeoutMs', v)}
                />
              </div>

              <div style={s.smsPickRow}>
                {[
                  { key: '', label: 'Account default', sub: 'Let BeSends decide' },
                  { key: 'api', label: 'API', sub: 'BeSends sends it' },
                  { key: 'android', label: 'Android', sub: 'A handset on the account' },
                ].map((option) => {
                  const chosen = (channel.method || '') === option.key;
                  return (
                    <button
                      key={option.key || 'default'}
                      style={chosen ? s.smsPickOn : s.smsPick}
                      onClick={() => updateSmsChannel(ci, 'method', option.key)}
                    >
                      <span style={{ fontWeight: 700 }}>
                        <i className={chosen ? 'fa fa-dot-circle-o' : 'fa fa-circle-o'} style={{ marginRight: 7 }} />
                        {option.label}
                      </span>
                      <span style={{ fontSize: 12, opacity: 0.75 }}>{option.sub}</span>
                    </button>
                  );
                })}
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {/* Free — no message is queued, so press it as often as you like. */}
                <button
                  style={s.secondaryBtn}
                  onClick={() => testSmsConnection(ci)}
                  disabled={busy === `sms-conn-${ci}`}
                >
                  {busy === `sms-conn-${ci}` ? 'Checking…' : 'Test connection (free)'}
                </button>
                {/* Costs real credit and is logged like any other send. Aimed at
                    THIS gateway, so a pass proves this one works rather than
                    that some SMS went out through another. */}
                <button
                  style={s.secondaryBtn}
                  onClick={() => runTest('pitech', channel._id)}
                  disabled={busy === 'test-pitech' || !channel._id}
                  title={channel._id ? '' : 'Save this gateway first'}
                >
                  {busy === 'test-pitech' ? 'Sending…' : 'Send a test SMS (costs credit)'}
                </button>
              </div>

              {/* WhatsApp through the same BeSends account and key. */}
              <div style={{ ...s.providerBox, background: '#f0fdf4', borderColor: '#bbf7d0', marginTop: 12 }}>
                <div style={s.gateTop}>
                  <strong>
                    <i className="fa fa-whatsapp" style={{ color: '#16a34a' }} /> WhatsApp on this gateway
                  </strong>
                  <Toggle
                    on={Boolean(channel.whatsappEnabled)}
                    onClick={() => updateSmsChannel(ci, 'whatsappEnabled', !channel.whatsappEnabled)}
                  />
                </div>
                <div style={s.hint}>
                  Sends through BeSends <code>/whatsapp/send</code> with the same API key. Used by the
                  Message Center when a message has WhatsApp in its channel order. The gateway itself
                  must also be switched on.
                </div>
                <div style={s.smsPickRow}>
                  {[
                    { key: 'auto', label: 'Auto', sub: 'Linked phone if online, else Meta numbers' },
                    { key: 'device', label: 'Linked phone', sub: 'Your QR-linked number, free text' },
                    { key: 'cloud', label: 'Meta Business', sub: 'Needs an approved template' },
                  ].map((option) => {
                    const chosen = (channel.whatsappRoute || 'auto') === option.key;
                    return (
                      <button
                        key={option.key}
                        style={chosen ? s.smsPickOn : s.smsPick}
                        onClick={() => updateSmsChannel(ci, 'whatsappRoute', option.key)}
                      >
                        <span style={{ fontWeight: 700 }}>
                          <i className={chosen ? 'fa fa-dot-circle-o' : 'fa fa-circle-o'} style={{ marginRight: 7 }} />
                          {option.label}
                        </span>
                        <span style={{ fontSize: 12, opacity: 0.75 }}>{option.sub}</span>
                      </button>
                    );
                  })}
                </div>
                <div style={s.grid}>
                  <Field
                    label="WhatsApp number identifier (optional)"
                    value={channel.whatsappGatewayIdentifier || ''}
                    onChange={(v) => updateSmsChannel(ci, 'whatsappGatewayIdentifier', v)}
                    placeholder="blank = account default number"
                  />
                  <Field
                    label="Approved template (Meta route, optional)"
                    value={channel.whatsappTemplate || ''}
                    onChange={(v) => updateSmsChannel(ci, 'whatsappTemplate', v)}
                    placeholder="e.g. besends_update"
                  />
                </div>
                <div style={s.hint}>
                  The template receives the message text as its first parameter <code>{'{{1}}'}</code>. Needed
                  only to reach people who have not messaged you in the last 24 hours on the Meta route.
                </div>
                <button
                  style={s.secondaryBtn}
                  onClick={() => runTest('besendswa', channel._id)}
                  disabled={busy === 'test-besendswa' || !channel._id}
                  title={channel._id ? '' : 'Save this gateway first'}
                >
                  {busy === 'test-besendswa' ? 'Sending…' : 'Send a test WhatsApp (costs credit)'}
                </button>
              </div>
              {!channel._id ? (
                <div style={s.hint}>
                  Save first to send a real test through this gateway. The free connection
                  check works on what is typed above without saving.
                </div>
              ) : null}
            </div>
          );
        })}

        <div style={s.testRow}>
          <Field label="Test phone number" value={testPhone} onChange={setTestPhone} placeholder="01719679996" />
        </div>
      </Section>

      {/* OTP BEHAVIOUR */}
      <Section title="OTP Behaviour">
        <div style={s.grid}>
          <Field label="Expiry (minutes)" type="number" value={settings.otp?.expiryMinutes} onChange={(v) => update('otp.expiryMinutes', v)} />
          <Field label="Resend countdown (seconds)" type="number" value={settings.otp?.resendCountdownSeconds} onChange={(v) => update('otp.resendCountdownSeconds', v)} />
          <Field
            label="Re-send an unused OTP (any gate) by SMS after (seconds, 0 = off)"
            type="number"
            value={settings.otp?.loginSmsFollowUpSeconds}
            onChange={(v) => update('otp.loginSmsFollowUpSeconds', v)}
          />
          <Field label="Max verify attempts" type="number" value={settings.otp?.maxVerifyAttempts} onChange={(v) => update('otp.maxVerifyAttempts', v)} />
          <Field label="Gateway cost per SMS (BDT)" type="number" value={settings.otp?.costBdt} onChange={(v) => update('otp.costBdt', v)} />
          <Field label="Gateway cost per WhatsApp (BDT)" type="number" value={settings.otp?.costBdtWhatsapp ?? 1} onChange={(v) => update('otp.costBdtWhatsapp', v)} />
          <Field label="Low-balance alert below (BDT)" type="number" value={settings.lowBalanceThreshold} onChange={(v) => update('lowBalanceThreshold', v)} />
          <Field label="OTP length" value="6 (fixed — two pairs)" onChange={() => {}} disabled />
        </div>
      </Section>

      {/* WHAT THE USER PAYS */}
      <Section
        title="What the Customer Pays per OTP"
        subtitle="Separate from the gateway cost above. That is what the send costs us; this is what the customer is charged. Set the amount to 0 to make OTPs free."
      >
        <div style={s.grid}>
          <Field
            label="Charge per SMS"
            type="number"
            value={settings.otp?.userChargeAmount}
            onChange={(v) => update('otp.userChargeAmount', v)}
          />
          <Field
            label="Charge per WhatsApp"
            type="number"
            value={settings.otp?.userChargeAmountWhatsapp ?? 1}
            onChange={(v) => update('otp.userChargeAmountWhatsapp', v)}
          />
          <label style={s.selectWrap}>
            <span style={s.selectLabel}>Take it from</span>
            <select
              style={s.select}
              value={settings.otp?.userChargeField || 'availablebalance'}
              onChange={(e) => update('otp.userChargeField', e.target.value)}
            >
              <option value="availablebalance">Available balance</option>
              <option value="point">Points</option>
              <option value="shoppingbalance">Shopping balance</option>
            </select>
          </label>
        </div>
        <div style={s.hint}>
          Charges taken from the available balance also appear in the customer's balance
          history. Points and shopping balance have no such ledger, so those charges are
          recorded on the OTP log only. Staff-initiated codes (a delivery hand-over) never
          bill the customer, whatever is set here.
        </div>
      </Section>

      {/* IDLE SIGN-OUT */}
      <Section
        title="Automatic Sign-Out"
        subtitle="Ends a session that has sat idle, so returning to the site or reopening the app requires signing in again."
      >
        <div style={s.gateTop}>
          <span>
            <strong>Sign idle users out</strong>
            <div style={s.hint}>
              Background polling does not count as activity, so leaving a tab open will not
              keep a session alive on its own.
            </div>
          </span>
          <Toggle
            on={settings.session?.idleLogoutEnabled !== false}
            onClick={() => update('session.idleLogoutEnabled', settings.session?.idleLogoutEnabled === false)}
          />
        </div>
        <div style={s.grid}>
          <Field
            label="Sign out after (minutes idle)"
            type="number"
            value={settings.session?.idleMinutes}
            onChange={(v) => update('session.idleMinutes', v)}
          />
        </div>
        <div style={s.gateTop}>
          <span>
            <strong>Apply to staff too</strong>
            <div style={s.hint}>
              Off by default — a counter waiting for customers should not be signed out
              mid-shift. Members are always covered.
            </div>
          </span>
          <Toggle
            on={Boolean(settings.session?.applyToStaff)}
            onClick={() => update('session.applyToStaff', !settings.session?.applyToStaff)}
          />
        </div>
        <div style={s.breakerWarn}>
          Turning this on signs <strong>everyone</strong> out once, including you. Tokens
          issued before this feature existed cannot be tracked, so they are retired the
          first time it takes effect.
        </div>
      </Section>

      {/* BALANCE RESERVE + ADMIN ALERTS */}
      <Section
        title="Balance Reserve & Admin Alerts"
        subtitle="When the balance drops below the reserve, ALL user OTP sending stops and the admin numbers are alerted. The reserve funds those alerts."
      >
        <div style={s.grid}>
          <Field label="Reserve floor (BDT) — block OTP below this" type="number" value={settings.reserveFloorBdt} onChange={(v) => update('reserveFloorBdt', v)} />
          <Field label="Alert cooldown (minutes)" type="number" value={settings.lowBalanceAlertCooldownMinutes} onChange={(v) => update('lowBalanceAlertCooldownMinutes', v)} />
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={s.fieldLabel}>Admin alert numbers (up to 3)</label>
          <div style={s.grid}>
            {[0, 1, 2].map((i) => (
              <input
                key={i}
                style={s.input}
                value={(settings.adminAlertNumbers || [])[i] || ''}
                onChange={(e) => setAdminNumber(i, e.target.value)}
                placeholder={`Admin ${i + 1} phone (e.g. 01719679996)`}
              />
            ))}
          </div>
        </div>
      </Section>

      {/* TEMPLATES */}
      <Section title="Message Templates" subtitle="Use {code} for the OTP and {minutes} for the expiry.">
        {GATES.map((g) => (
          <div key={g.key} style={{ marginBottom: 12 }}>
            <label style={s.fieldLabel}>{g.label}</label>
            <textarea
              style={s.textarea}
              value={settings.templates?.[g.key] || ''}
              onChange={(e) => update(`templates.${g.key}`, e.target.value)}
            />
          </div>
        ))}
        <div style={{ marginBottom: 12 }}>
          <label style={s.fieldLabel}>Admin low-balance alert — use {'{balance}'} and {'{reserve}'}</label>
          <textarea
            style={s.textarea}
            value={settings.templates?.adminLowBalance || ''}
            onChange={(e) => update('templates.adminLowBalance', e.target.value)}
          />
        </div>
      </Section>

      {/* BALANCE + PIN */}
      <Section title="Balance & Access">
        <div style={s.grid}>
          <div style={s.miniCard}>
            <label style={s.fieldLabel}>Top up / adjust balance (BDT)</label>
            <div style={s.inlineRow}>
              <input style={s.input} type="number" value={topupAmount} onChange={(e) => setTopupAmount(e.target.value)} placeholder="e.g. 2000 (negative to deduct)" />
              <button style={s.secondaryBtn} onClick={topup} disabled={busy === 'topup'}>Apply</button>
            </div>
            <p style={s.muted}>Current: ৳ {balance.toFixed(2)}</p>
          </div>
          <div style={s.miniCard}>
            <label style={s.fieldLabel}>Change developer PIN</label>
            <div style={s.inlineRow}>
              <input style={s.input} type="password" inputMode="numeric" value={newPin} onChange={(e) => setNewPin(e.target.value)} placeholder="New PIN (5-8 digits)" />
              <button style={s.secondaryBtn} onClick={changePin} disabled={busy === 'pin'}>Update</button>
            </div>
          </div>
        </div>
      </Section>

      {/* OTP LOG */}
      <Section title="OTP Log" subtitle="Most recent codes issued.">
        <button style={s.secondaryBtn} onClick={loadLog} disabled={busy === 'log'}>
          {busy === 'log' ? 'Loading…' : otpLog ? 'Refresh log' : 'Load log'}
        </button>
        {otpLog && (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>When</th><th style={s.th}>User</th><th style={s.th}>Type</th>
                  <th style={s.th}>Status</th><th style={s.th}>Attempts</th><th style={s.th}>Cost</th><th style={s.th}>Channels</th>
                </tr>
              </thead>
              <tbody>
                {otpLog.rows.map((r) => (
                  <tr key={r._id}>
                    <td style={s.td}>{new Date(r.createdAt).toLocaleString()}</td>
                    <td style={s.td}>{r.username || r.phone}</td>
                    <td style={s.td}>{r.type}</td>
                    <td style={s.td}>{r.status}</td>
                    <td style={s.td}>{r.attempts}/{r.maxAttempts}</td>
                    <td style={s.td}>৳{Number(r.cost || 0).toFixed(2)}</td>
                    <td style={s.td}>
                      {(r.channels || [])
                        .map((c) => `${otpProviderLabel(c.provider)}:${c.ok ? '✓' : c.mayHaveDelivered ? '?' : '✗'}`)
                        .join(' ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section
        title="Mobile App Runtime"
        subtitle="This affects the React Native app immediately after its next config refresh."
      >
        {!appConfig?.app ? (
          <p style={s.muted}>Runtime config is not loaded yet.</p>
        ) : (
          <>
            <div style={s.grid}>
              <SwitchRow
                label="App globally enabled"
                hint="Off blocks non-admin devices with maintenance mode."
                on={appConfig.app.enabled !== false}
                onToggle={(v) => updateAppConfig('app.enabled', v)}
              />
              <SwitchRow
                label="User login enabled"
                hint="Off blocks sign-in for non-admin users in the mobile app."
                on={appConfig.app.loginEnabled !== false}
                onToggle={(v) => updateAppConfig('app.loginEnabled', v)}
              />
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
              <button style={s.secondaryBtn} onClick={saveAppRuntime} disabled={configSaving}>
                {configSaving ? 'Saving…' : 'Save runtime toggles'}
              </button>
              <button
                style={s.secondaryBtn}
                onClick={async () => {
                  try {
                    await loadAppControlData();
                    toast.success('Reloaded from server.');
                  } catch (err) {
                    toast.error(getError(err));
                  }
                }}
              >
                Reload from server
              </button>
            </div>
          </>
        )}
      </Section>

      <Section
        title="Mobile Screen / Action Control"
        subtitle="Disable any screen, section, or action key from the app registry."
      >
        <h6 style={s.subhead}>Top 50 Quick Switches (Mobile Parity)</h6>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <button style={s.secondaryBtn} onClick={() => setTop50Enabled(true)}>Enable all 50</button>
          <button style={s.secondaryBtn} onClick={() => setTop50Enabled(false)}>Disable all 50</button>
        </div>
        <div style={s.top50Grid}>
          {top50Defs.map((def) => {
            const c = resolveControl(def);
            return (
              <div key={def.key} style={s.top50Card}>
                <div style={{ minWidth: 0 }}>
                  <strong style={{ fontSize: 13, display: 'block' }}>{def.label || def.key}</strong>
                  <span style={{ ...s.muted, display: 'block', marginTop: 2, wordBreak: 'break-all' }}>{def.key}</span>
                </div>
                <Toggle
                  on={c.enabled}
                  onClick={() => { if (!def.locked) updateControl(def, 'enabled', !c.enabled); }}
                />
              </div>
            );
          })}
        </div>

        <h6 style={{ ...s.subhead, marginTop: 16 }}>Full Registry</h6>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) auto', gap: 8, marginBottom: 12 }}>
          <input
            style={s.input}
            placeholder="Search controls: signIn, member.shop, admin.users..."
            value={controlQuery}
            onChange={(e) => setControlQuery(e.target.value)}
          />
          <button style={s.secondaryBtn} onClick={saveControls} disabled={controlsSaving}>
            {controlsSaving ? 'Saving…' : `Save ${Object.keys(controlState || {}).length} controls`}
          </button>
        </div>

        <div style={s.tableWrap}>
          <table style={{ ...s.table, minWidth: 980 }}>
            <thead>
              <tr>
                <th style={s.th}>Role / Area</th>
                <th style={s.th}>Control</th>
                <th style={s.th}>Enabled</th>
                <th style={s.th}>Hidden</th>
                <th style={s.th}>Message</th>
              </tr>
            </thead>
            <tbody>
              {visibleControls.map((def) => {
                const c = resolveControl(def);
                return (
                  <tr key={def.key}>
                    <td style={s.td}>
                      <div style={{ fontWeight: 700 }}>{def.role || 'all'}</div>
                      <div style={{ color: '#6b7280', fontSize: 12 }}>{def.area || '-'}</div>
                    </td>
                    <td style={s.td}>
                      <div style={{ fontWeight: 700 }}>{def.label || def.key}</div>
                      <div style={{ color: '#6b7280', fontSize: 12 }}>{def.key}</div>
                      {!!def.locked && (
                        <span style={s.badge}>locked</span>
                      )}
                    </td>
                    <td style={s.td}>
                      <Toggle
                        on={c.enabled}
                        onClick={() => { if (!def.locked) updateControl(def, 'enabled', !c.enabled); }}
                      />
                    </td>
                    <td style={s.td}>
                      <Toggle
                        on={c.hidden}
                        onClick={() => updateControl(def, 'hidden', !c.hidden)}
                      />
                    </td>
                    <td style={s.td}>
                      <input
                        style={s.input}
                        value={c.message}
                        onChange={(e) => updateControl(def, 'message', e.target.value)}
                        placeholder="Shown when blocked"
                      />
                    </td>
                  </tr>
                );
              })}
              {!visibleControls.length && (
                <tr>
                  <td style={s.td} colSpan={5}>No controls matched your search.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      <div style={s.saveBar}>
        <button style={s.primaryBtn} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save all settings'}
        </button>
      </div>

    </div>
  );
}

// Full sticker designer: paper size, which lines appear, and their wording,
// with a live preview rendered by the same code that writes the PDF.
function StickerDesigner({ design, onChange }) {
  const d = resolveSticker(design);
  const plan = sheetPlan(d);
  const presetId = presetIdFor(d.widthMm, d.heightMm);

  // A believable sample so the preview shows real wrapping and a real barcode.
  const sampleUnit = {
    serial: 'CC-2026-000123',
    batchCode: 'BATCH-000001',
    createdAt: new Date(),
  };
  const sampleProduct = {
    name: 'Premium Vitamin C Complex 500mg',
    price: 1250,
    point: 50,
    brand: 'ClickCart',
    category: 'Health',
  };

  let preview = '';
  let previewError = '';
  try {
    preview = stickerPreviewSvg({
      unit: sampleUnit,
      product: sampleProduct,
      sticker: d,
      maxPx: 230,
    });
  } catch (err) {
    previewError = err.message;
  }

  return (
    <Section
      title="Sticker Design"
      subtitle="Everything printed on a product sticker. Anything switched off is removed and the rest re-flows, so the design works on an A4 sheet and on a small thermal roll alike."
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 20, alignItems: 'start' }}>
        <div style={{ minWidth: 0 }}>
          {/* ---- paper ---- */}
          <h6 style={s.subhead}>Paper</h6>
          <div style={s.grid}>
            <div style={{ marginBottom: 10 }}>
              <label style={s.fieldLabel}>Output</label>
              <select style={s.input} value={d.format} onChange={(e) => onChange('format', e.target.value)}>
                <option value="a4">A4 sheet — tile many per page</option>
                <option value="label">Label roll — one per page (thermal printer)</option>
              </select>
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={s.fieldLabel}>Label size</label>
              <select
                style={s.input}
                value={presetId}
                onChange={(e) => {
                  const preset = STICKER_PRESETS.find((p) => p.id === e.target.value);
                  if (!preset) return;
                  onChange('widthMm', preset.widthMm);
                  onChange('heightMm', preset.heightMm);
                }}
              >
                {STICKER_PRESETS.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
                <option value="custom">Custom…</option>
              </select>
            </div>
            <Field label="Width (mm)" type="number" value={d.widthMm} onChange={(v) => onChange('widthMm', v)} />
            <Field label="Height (mm)" type="number" value={d.heightMm} onChange={(v) => onChange('heightMm', v)} />
            {d.format === 'a4' && (
              <>
                <Field label="Gap between stickers (mm)" type="number" value={d.gapMm} onChange={(v) => onChange('gapMm', v)} />
                <Field label="Page margin (mm)" type="number" value={d.pageMarginMm} onChange={(v) => onChange('pageMarginMm', v)} />
              </>
            )}
          </div>

          {/* ---- elements ---- */}
          <h6 style={{ ...s.subhead, marginTop: 18 }}>What appears on the sticker</h6>
          <div style={s.grid}>
            {STICKER_ELEMENTS.map((item) => (
              <SwitchRow
                key={item.key}
                label={item.label}
                hint={item.hint}
                on={Boolean(d[item.key])}
                onToggle={(next) => onChange(item.key, next)}
              />
            ))}
          </div>

          {/* ---- wording ---- */}
          <h6 style={{ ...s.subhead, marginTop: 18 }}>Wording &amp; sizing</h6>
          <div style={s.grid}>
            <Field
              label="Header text"
              value={d.headerText}
              onChange={(v) => onChange('headerText', v)}
              placeholder="Your shop name"
              disabled={!d.showHeader}
            />
            <Field
              label="Company name (footer)"
              value={d.footerText}
              onChange={(v) => onChange('footerText', v)}
              placeholder="ClickCart"
              disabled={!d.showFooter}
            />
            <Field
              label="Currency label"
              value={d.currencyLabel}
              onChange={(v) => onChange('currencyLabel', v)}
              placeholder="Tk"
              disabled={!d.showPrice}
            />
            <Field
              label="Points label"
              value={d.pointLabel}
              onChange={(v) => onChange('pointLabel', v)}
              placeholder="PV"
              disabled={!d.showPoint}
            />
            <div style={{ marginBottom: 10 }}>
              <label style={s.fieldLabel}>Date format</label>
              <select
                style={s.input}
                value={d.dateFormat}
                disabled={!d.showDate}
                onChange={(e) => onChange('dateFormat', e.target.value)}
              >
                <option value="date">Date only — 28/07/2026</option>
                <option value="datetime">Date and time — 28/07/2026 14:30</option>
                <option value="time">Time only — 14:30</option>
              </select>
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={s.fieldLabel}>Product name lines</label>
              <select
                style={s.input}
                value={d.nameMaxLines}
                disabled={!d.showProductName}
                onChange={(e) => onChange('nameMaxLines', Number(e.target.value))}
              >
                <option value={1}>1 line</option>
                <option value={2}>2 lines</option>
                <option value={3}>3 lines</option>
              </select>
            </div>
            <Field
              label="Barcode height (% of sticker)"
              type="number"
              value={d.barcodeHeightPct}
              onChange={(v) => onChange('barcodeHeightPct', v)}
              disabled={!d.showBarcode}
            />
            <Field
              label="Text size (0.6 – 1.6)"
              type="number"
              value={d.textScale}
              onChange={(v) => onChange('textScale', v)}
            />
          </div>
        </div>

        {/* ---- live preview ---- */}
        <div style={{ width: 260, flex: 'none' }}>
          <label style={s.fieldLabel}>Live preview — actual proportions</label>
          <div style={s.previewBox}>
            {previewError ? (
              <span style={{ color: '#b91c1c', fontSize: 12 }}>{previewError}</span>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: preview }} />
            )}
          </div>

          <div style={s.previewFacts}>
            <Fact label="Size" value={`${Number(d.widthMm).toFixed(1)} × ${Number(d.heightMm).toFixed(1)} mm`} />
            {d.format === 'a4' ? (
              <Fact
                label="Per A4 page"
                value={plan.fitsOnA4 ? `${plan.perPage}  (${plan.columns} × ${plan.rows})` : 'does not fit'}
                bad={!plan.fitsOnA4}
              />
            ) : (
              <Fact label="Per page" value="1 (label roll)" />
            )}
            {d.showBarcode && (
              <Fact
                label="Barcode bar width"
                value={`${plan.moduleMm.toFixed(2)} mm`}
                bad={!plan.barcodeOk && !plan.barcodeTight}
                warn={plan.barcodeTight}
              />
            )}
          </div>

          {d.format === 'a4' && !plan.fitsOnA4 && (
            <p style={s.previewWarn}>
              This label is too big for an A4 sheet with the current margin. Reduce the size or the
              margin, or switch to label-roll output.
            </p>
          )}
          {d.showBarcode && !plan.barcodeOk && (
            <p style={s.previewWarn}>
              {plan.barcodeTight
                ? 'Bars are narrow — most scanners cope, but a wider label or a shorter serial prefix reads more reliably.'
                : 'Bars are too narrow to scan reliably. Widen the label, or shorten the serial prefix and digit count.'}
            </p>
          )}
          {!d.showBarcode && (
            <p style={s.previewWarn}>
              With the barcode off, units cannot be scanned at the delivery counter — staff would have
              to type each serial by hand.
            </p>
          )}
        </div>
      </div>
    </Section>
  );
}

function Fact({ label, value, bad, warn }) {
  return (
    <div style={s.fact}>
      <span style={{ color: '#6b7280' }}>{label}</span>
      <strong style={{ color: bad ? '#b91c1c' : warn ? '#b45309' : '#111827' }}>{value}</strong>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div style={s.section}>
      <div style={{ marginBottom: 12 }}>
        <h5 style={{ margin: 0 }}>{title}</h5>
        {subtitle && <p style={s.muted}>{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text', secret, disabled }) {
  const [reveal, setReveal] = useState(false);
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={s.fieldLabel}>{label}</label>
      <div style={s.inlineRow}>
        <input
          style={s.input}
          type={secret && !reveal ? 'password' : type}
          value={value ?? ''}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
        {secret && (
          <button type="button" style={s.eyeBtn} onClick={() => setReveal((r) => !r)}>
            <i className={`fa ${reveal ? 'fa-eye-slash' : 'fa-eye'}`} />
          </button>
        )}
      </div>
    </div>
  );
}

// A labelled switch with an explanation — used by the delivery, fingerprint
// and stock sections where each flag needs a sentence to be unambiguous.
function SwitchRow({ label, hint, on, onToggle }) {
  return (
    <div style={s.switchRow} onClick={() => onToggle(!on)} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(!on); } }}
    >
      <div style={{ minWidth: 0 }}>
        <strong style={{ fontSize: 13, display: 'block' }}>{label}</strong>
        {hint && <span style={{ ...s.muted, display: 'block', marginTop: 2 }}>{hint}</span>}
      </div>
      <Toggle on={on} onClick={(e) => { e?.stopPropagation?.(); onToggle(!on); }} />
    </div>
  );
}

function Toggle({ on, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{ ...s.toggle, ...(on ? s.toggleOn : s.toggleOff) }}>
      <span style={{ ...s.knob, transform: on ? 'translateX(20px)' : 'translateX(0)' }} />
    </button>
  );
}

const s = {
  wrap: { maxWidth: 1000, margin: '0 auto', padding: '20px 16px 80px' },
  gateWrap: { minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  gateCard: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 30, width: 340, textAlign: 'center', boxShadow: '0 10px 30px rgba(0,0,0,0.06)' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap', marginBottom: 18 },
  muted: { color: '#6b7280', fontSize: 13, margin: '4px 0 0' },
  section: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 18, marginBottom: 16 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 },
  gateCardMini: { border: '1px solid #eef0f3', borderRadius: 10, padding: 12 },
  subhead: {
    margin: '0 0 10px',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#6b7280',
  },
  previewBox: {
    display: 'grid',
    placeItems: 'center',
    background: '#f8fafc',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 16,
    minHeight: 240,
  },
  previewFacts: { marginTop: 12, display: 'grid', gap: 6 },
  fact: { display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12 },
  previewWarn: {
    marginTop: 10,
    fontSize: 11.5,
    lineHeight: 1.45,
    color: '#92400e',
    background: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: 8,
    padding: '8px 10px',
  },
  switchRow: {
    border: '1px solid #eef0f3',
    borderRadius: 10,
    padding: 12,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    cursor: 'pointer',
    background: '#fff',
  },
  top50Grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: 10 },
  top50Card: {
    border: '1px solid #eef0f3',
    borderRadius: 10,
    padding: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    background: '#fff',
  },
  gateTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  checkRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151' },
  providerBox: { border: '1px solid #eef0f3', borderRadius: 10, padding: 14, marginBottom: 12 },
  smsPickRow: { display: 'flex', gap: 10, flexWrap: 'wrap', margin: '10px 0' },
  smsPick: { flex: '1 1 220px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, textAlign: 'left', border: '1px solid #d1d5db', background: '#fff', borderRadius: 10, padding: '10px 13px', cursor: 'pointer', color: '#111827' },
  smsPickOn: { flex: '1 1 220px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, textAlign: 'left', border: '1px solid #2563eb', background: '#eff6ff', borderRadius: 10, padding: '10px 13px', cursor: 'pointer', color: '#1e3a8a' },
  primaryTag: { marginLeft: 8, background: '#dbeafe', color: '#1d4ed8', borderRadius: 999, padding: '1px 8px', fontSize: 10, fontWeight: 800, letterSpacing: '.04em' },
  warnBox: { background: '#fef3c7', border: '1px solid #fcd34d', color: '#92400e', borderRadius: 9, padding: '9px 11px', fontSize: 12.5, margin: '8px 0' },
  priorityTag: { marginLeft: 8, fontSize: 11, fontWeight: 700, color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' },
  sessionHead: { marginTop: 14, marginBottom: 6, fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.4 },
  sessionRow: { border: '1px solid #e5e7eb', borderRadius: 9, padding: 12, marginBottom: 10, background: '#fafafa' },
  sessionTop: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 },
  sessionRank: { fontSize: 12, fontWeight: 700, color: '#111827' },
  pillOk: { fontSize: 11, fontWeight: 700, color: '#166534', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 999, padding: '2px 8px' },
  pillWarn: { fontSize: 11, fontWeight: 700, color: '#9a3412', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 999, padding: '2px 8px' },
  pillBad: { fontSize: 11, fontWeight: 700, color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 999, padding: '2px 8px' },
  dangerLink: { background: 'none', border: 'none', color: '#b91c1c', fontWeight: 700, cursor: 'pointer', padding: 0, marginLeft: 6, textDecoration: 'underline' },
  breakerWarn: { background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412', borderRadius: 8, padding: '10px 12px', margin: '10px 0', fontSize: 13, lineHeight: 1.5 },
  sessionOk: { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', borderRadius: 8, padding: '10px 12px', margin: '10px 0', fontSize: 13, lineHeight: 1.5 },
  sessionActions: { display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', marginTop: 8 },
  // Linking a number is the primary action on this panel, so it gets WhatsApp's
  // own green rather than sitting in the row of grey secondary buttons.
  scanBtn: { background: '#25d366', color: '#0b3d20', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 700, cursor: 'pointer' },
  dangerBtn: { background: '#b91c1c', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, cursor: 'pointer' },
  iconBtn: { background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, width: 32, height: 32, lineHeight: '30px', padding: 0, cursor: 'pointer' },
  createBox: { border: '1px dashed #cbd5e1', borderRadius: 10, padding: 14, marginTop: 6, background: '#f8fafc' },
  createHead: { fontSize: 13, fontWeight: 700, color: '#111827', marginBottom: 4 },
  pairBox: { marginTop: 14, paddingTop: 14, borderTop: '1px solid #e5e7eb' },
  pairInput: { flex: 1, minWidth: 150, border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 10px', fontSize: 14 },
  // Read aloud over a phone call more often than not, so it is spaced and large.
  pairCode: { fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 26, fontWeight: 800, letterSpacing: 4, textAlign: 'center', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 10, padding: '14px 10px', margin: '10px 0' },
  remoteBox: { marginTop: 10, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', fontSize: 13, lineHeight: 1.6 },
  diagRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 3000 },
  modal: { background: '#fff', borderRadius: 14, padding: 20, width: '100%', maxWidth: 420, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.25)' },
  modalHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 },
  qrImg: { display: 'block', width: '100%', maxWidth: 320, margin: '0 auto', border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff' },
  qrSteps: { margin: '14px 0 4px', paddingLeft: 20, fontSize: 13, lineHeight: 1.7, color: '#374151' },
  qrWait: { textAlign: 'center', padding: '28px 10px', fontSize: 14, color: '#374151' },
  linkedBox: { textAlign: 'center', padding: '24px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 },
  hint: { color: '#6b7280', fontSize: 12, lineHeight: 1.45, marginTop: 2, maxWidth: 460 },
  selectWrap: { display: 'flex', flexDirection: 'column', gap: 6 },
  selectLabel: { fontSize: 12, fontWeight: 600, color: '#374151' },
  select: { border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 10px', fontSize: 14, background: '#fff' },
  linkBtn: { background: 'none', border: 'none', color: '#b45309', fontWeight: 700, cursor: 'pointer', padding: 0, marginLeft: 6, textDecoration: 'underline' },
  testRow: { maxWidth: 320 },
  miniCard: { border: '1px solid #eef0f3', borderRadius: 10, padding: 12 },
  fieldLabel: { display: 'block', fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 5 },
  input: { width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 11px', fontSize: 14, background: '#fff', color: '#111827' },
  textarea: { width: '100%', border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 11px', fontSize: 13, minHeight: 60 },
  inlineRow: { display: 'flex', gap: 8, alignItems: 'center' },
  eyeBtn: { border: '1px solid #d1d5db', background: '#f9fafb', borderRadius: 8, padding: '8px 10px', cursor: 'pointer' },
  primaryBtn: { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 18px', fontWeight: 700, cursor: 'pointer', width: '100%' },
  secondaryBtn: { background: '#f3f4f6', color: '#111827', border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 14px', fontWeight: 600, cursor: 'pointer' },
  saveBar: { position: 'sticky', bottom: 0, padding: '14px 0', background: 'linear-gradient(transparent, #fff 40%)' },
  balancePill: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', borderRadius: 12, padding: '10px 16px', minWidth: 150 },
  balanceOk: { background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46' },
  balanceDanger: { background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b' },
  toggle: { width: 44, height: 24, borderRadius: 999, border: 'none', position: 'relative', cursor: 'pointer', padding: 2 },
  toggleOn: { background: '#22c55e' },
  toggleOff: { background: '#cbd5e1' },
  knob: { display: 'block', width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'transform 0.15s' },
  tableWrap: { overflowX: 'auto', marginTop: 12 },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 720 },
  th: { textAlign: 'left', padding: '8px 10px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 11, textTransform: 'uppercase', color: '#6b7280' },
  td: { padding: '8px 10px', borderBottom: '1px solid #f3f4f6', fontSize: 13 },
  badge: {
    display: 'inline-block',
    marginTop: 5,
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: '#334155',
    background: '#e2e8f0',
    borderRadius: 999,
    padding: '2px 7px',
  },
};
